import {
  buildCatalogEquivalenceGroups,
  getCatalogVitrinePrimary,
  pickCatalogVitrinePrimary,
} from './catalogEquivalenceGroup';
import { catalogProductHasPublishedModule } from './productCatalog';

export function hasCatalogVitrinePhoto(product) {
  return !!product?.catalogImage?.url;
}

export function hasCatalogVitrineCategory(product) {
  return !!product?.productCategoryId;
}

/** Produto configurado para aparecer na vitrine (showInCatalog !== false). */
export function isCatalogVitrineListed(product) {
  return product?.showInCatalog !== false;
}

function collectMissingProducts(missingGroups = []) {
  return [
    ...new Map(
      missingGroups.flatMap((members) => members.map((product) => [product.id, product]))
    ).values(),
  ];
}

/**
 * Cada grupo de equivalentes (ou produto isolado) com pelo menos um membro visível
 * na vitrine precisa de capa e categoria entre os listados. Produtos com
 * showInCatalog=false e grupos só com ocultos não entram na validação.
 */
export function getCatalogVitrineReadiness(catalogProducts = []) {
  const { groups } = buildCatalogEquivalenceGroups(catalogProducts);
  const missingPhotoGroups = [];
  const missingCategoryGroups = [];

  groups.forEach((members) => {
    const listedMembers = members.filter(isCatalogVitrineListed);
    if (listedMembers.length === 0) return;

    if (!listedMembers.some(hasCatalogVitrinePhoto)) {
      missingPhotoGroups.push(listedMembers);
    }
    if (!listedMembers.some(hasCatalogVitrineCategory)) {
      missingCategoryGroups.push(listedMembers);
    }
  });

  const missingPhotoProducts = collectMissingProducts(missingPhotoGroups);
  const missingCategoryProducts = collectMissingProducts(missingCategoryGroups);
  const missingProducts = collectMissingProducts([
    ...missingPhotoGroups,
    ...missingCategoryGroups,
  ]);

  return {
    ready: missingPhotoGroups.length === 0 && missingCategoryGroups.length === 0,
    missingGroups: missingPhotoGroups,
    missingPhotoGroups,
    missingCategoryGroups,
    missingProducts,
    missingPhotoProducts,
    missingCategoryProducts,
  };
}

/** Nomes únicos dos produtos/planos/cursos com pendências na vitrine (para exibir na UI). */
export function getCatalogVitrineReadinessProductNames({ missingProducts = [] } = {}) {
  return [...missingProducts]
    .map((product) => product.name || product.id)
    .sort((a, b) => a.localeCompare(b, 'pt-BR'));
}

/** Listas separadas por tipo de pendência (capa x categoria) para banners do admin. */
export function getCatalogVitrineReadinessIssues(readiness = {}) {
  return {
    missingPhotoProductNames: getCatalogVitrineReadinessProductNames({
      missingProducts: readiness.missingPhotoProducts ?? [],
    }),
    missingCategoryProductNames: getCatalogVitrineReadinessProductNames({
      missingProducts: readiness.missingCategoryProducts ?? [],
    }),
  };
}

/**
 * Showcase cards (one per equivalence group, with cover + category) hidden from
 * students because no module linked to the group is published. Warning only —
 * it never blocks enabling the showcase (admins prepare products with archived
 * modules on purpose). Groups whose members are all archived are skipped: they
 * are off sale and already hidden for non-buyers.
 */
export function getCatalogVitrineMissingPublishedModuleNames(
  catalogProducts = [],
  publishedCards = []
) {
  const { groups } = buildCatalogEquivalenceGroups(catalogProducts);
  const names = [];

  groups.forEach((members) => {
    if (members.every((product) => product.archived)) return;

    const primary = pickCatalogVitrinePrimary(members);
    if (!primary) return;

    if (!catalogProductHasPublishedModule(primary, publishedCards)) {
      names.push(primary.name || primary.id);
    }
  });

  return names.sort((a, b) => a.localeCompare(b, 'pt-BR'));
}

/** @deprecated Prefer getCatalogVitrineReadinessProductNames + CatalogVitrineReadinessBanner */
export function formatCatalogVitrineReadinessMessage({ missingGroups = [] } = {}) {
  if (!missingGroups.length) return '';

  const names = getCatalogVitrineReadinessProductNames({
    missingProducts: missingGroups.flatMap((members) => members),
  });

  return `Não é possível ativar a Vitrine de Produtos, pois os seguintes produtos visíveis na vitrine não estão com imagem de capa e categoria configuradas na aba Produtos: ${names.join(', ')}`;
}

export function needsCatalogVitrineHeirOnDelete(
  targetCatalogProduct,
  catalogProducts = [],
  hasProductCatalogEnabled
) {
  if (!hasProductCatalogEnabled || !targetCatalogProduct) return false;

  const { getGroupMembers } = buildCatalogEquivalenceGroups(catalogProducts);
  const members = getGroupMembers(targetCatalogProduct.id);
  if (members.length <= 1) return false;

  const primary = getCatalogVitrinePrimary(targetCatalogProduct, catalogProducts);
  return primary?.id === targetCatalogProduct.id;
}

export function getCatalogVitrineHeirCandidates(targetCatalogProduct, catalogProducts = []) {
  if (!targetCatalogProduct) return [];

  const { getGroupMembers } = buildCatalogEquivalenceGroups(catalogProducts);
  return getGroupMembers(targetCatalogProduct.id).filter(
    (product) => product.id !== targetCatalogProduct.id
  );
}

/** Usado em testes e diagnósticos: quem seria o representante se o grupo fosse exibido. */
export function previewCatalogVitrinePrimary(members = []) {
  return pickCatalogVitrinePrimary(members);
}
